import { useEffect, useMemo, useState } from "react";
import { Search, GraduationCap, ChevronRight, ShoppingCart, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";
import { api, formatR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { BackButton } from "@/components/BackButton";

export default function SchoolsPage() {
  const [schools, setSchools] = useState([]);
  const [q, setQ] = useState("");
  const [school, setSchool] = useState(null);
  const [grade, setGrade] = useState(null);
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState({});
  const [qtys, setQtys] = useState({});
  const [loadingList, setLoadingList] = useState(false);
  const { add, setDrawerOpen, setSchoolMeta } = useCart();

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/schools", { params: q ? { q } : {} }).then((r) => setSchools(r.data.schools)).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!school || !grade) return;
    setLoadingList(true);
    api
      .get(`/schools/${school.id}/list`, { params: { grade } })
      .then((r) => {
        setList(r.data);
        const sel = {}, qs = {};
        r.data.items.forEach((it, idx) => {
          sel[idx] = it.required && !it.unavailable;
          qs[idx] = it.qty;
        });
        setSelected(sel);
        setQtys(qs);
      })
      .catch(() => setList({ items: [] }))
      .finally(() => setLoadingList(false));
  }, [school, grade]);

  const totals = useMemo(() => {
    if (!list) return { count: 0, total: 0 };
    let count = 0, total = 0;
    list.items.forEach((it, idx) => {
      if (selected[idx] && !it.unavailable) {
        count += 1;
        total += (it.effective_price || 0) * (qtys[idx] || 1);
      }
    });
    return { count, total };
  }, [list, selected, qtys]);

  const addSelected = (all) => {
    if (!list) return;
    let added = 0;
    list.items.forEach((it, idx) => {
      const want = all ? !it.unavailable : selected[idx] && !it.unavailable;
      if (want) {
        add({ id: it.product_id, name: it.name, effective_price: it.effective_price, image: it.image, stock: it.stock }, qtys[idx] || 1, { silent: true });
        added += 1;
      }
    });
    if (added > 0) {
      if (school && grade) setSchoolMeta({ school_id: school.id, school_name: school.name, school_grade: grade });
      toast.success(`${added} item${added !== 1 ? "s" : ""} added from the ${school?.name} Grade ${grade} list`);
      setDrawerOpen(true);
    } else {
      toast.error("Select at least one available item");
    }
  };

  const sections = useMemo(() => {
    if (!list) return [];
    const groups = {};
    list.items.forEach((it, idx) => {
      const sec = it.section || "stationery";
      groups[sec] = groups[sec] || [];
      groups[sec].push({ ...it, idx });
    });
    return Object.entries(groups);
  }, [list]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="schools-page">
      <Seo title="Assorted Schools — Prescribed Grade Lists" description="Find your school's prescribed stationery and textbook list by grade and add the full pack to your cart at BTMEDU." />
      <Reveal>
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-brand mb-2 flex items-center gap-2">
          <GraduationCap size={14} /> Assorted Schools
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy">
          School & grade shopping
        </h1>
        <p className="text-sm text-slate-500 mt-2 max-w-xl">
          Choose your school and grade to see the exact prescribed stationery and textbook list.
        </p>
      </Reveal>

      <div className="mt-8 flex items-center gap-2 text-xs font-semibold flex-wrap" data-testid="school-steps">
        {["1 · School", "2 · Grade", "3 · Prescribed list"].map((s, i) => {
          const active = i === 0 ? !school : i === 1 ? school && !grade : school && grade;
          return (
            <span key={s} className={`px-3.5 py-1.5 rounded-full flex items-center gap-1.5 ${active ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-500"}`}>
              {s} {i < 2 && <ChevronRight size={12} />}
            </span>
          );
        })}
      </div>

      {!school && (
        <div className="mt-8" data-testid="school-picker">
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search schools by name or area…"
              className="w-full h-12 rounded-full bg-white border border-slate-200 pl-11 pr-4 text-sm outline-none focus:ring-2 focus:ring-gold"
              data-testid="school-search-input"
            />
          </div>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {schools.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.05}>
                <button
                  onClick={() => setSchool(s)}
                  className="w-full text-left bg-white rounded-2xl border border-slate-200/80 p-6 hover:border-gold hover:-translate-y-0.5 transition-[transform,border-color] group"
                  data-testid={`school-card-${s.id}`}
                >
                  <span className="w-10 h-10 rounded-xl bg-cream flex items-center justify-center text-brand group-hover:bg-gold/20 transition-colors">
                    <GraduationCap size={18} />
                  </span>
                  <h3 className="font-display font-bold text-navy mt-3">{s.name}</h3>
                  <p className="text-xs text-slate-500 mt-1">{s.location}</p>
                  <p className="text-[11px] font-semibold text-brand mt-2">Grades {s.grades.join(", ")}</p>
                </button>
              </Reveal>
            ))}
            {schools.length === 0 && (
              <p className="text-sm text-slate-500 col-span-full py-8" data-testid="no-schools">No schools match that search.</p>
            )}
          </div>
        </div>
      )}

      {school && !grade && (
        <div className="mt-8" data-testid="grade-picker">
          <div className="mb-4">
            <BackButton onClick={() => setSchool(null)} label="Back to schools" testId="back-to-schools" />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display text-xl font-bold text-navy">{school.name}</h2>
            <button onClick={() => setSchool(null)} className="text-xs font-semibold text-brand hover:underline" data-testid="change-school">
              Change school
            </button>
          </div>
          <p className="text-sm text-slate-500 mt-1">Select a grade to load the prescribed list.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            {school.grades.map((g) => (
              <button
                key={g}
                onClick={() => setGrade(g)}
                className="h-14 px-6 rounded-2xl bg-white border border-slate-200/80 font-display font-bold text-navy hover:border-gold hover:-translate-y-0.5 transition-[transform,border-color]"
                data-testid={`grade-${g}`}
              >
                Grade {g}
              </button>
            ))}
          </div>
        </div>
      )}

      {school && grade && (
        <div className="mt-8" data-testid="prescribed-list">
          <div className="mb-4 flex items-center gap-2 flex-wrap">
            <BackButton onClick={() => { setGrade(null); setList(null); }} label="Back to grades" testId="back-to-grades" />
            <BackButton onClick={() => { setSchool(null); setGrade(null); setList(null); }} label="All schools" testId="back-to-all-schools" />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display text-xl font-bold text-navy">
              {school.name} — Grade {grade}
            </h2>
            <button onClick={() => { setGrade(null); setList(null); }} className="text-xs font-semibold text-brand hover:underline" data-testid="change-grade">
              Change grade
            </button>
            <button onClick={() => { setSchool(null); setGrade(null); setList(null); }} className="text-xs font-semibold text-brand hover:underline" data-testid="restart-schools">
              Start over
            </button>
          </div>

          {loadingList ? (
            <div className="mt-6 bg-white rounded-3xl border border-slate-100 h-64 animate-pulse" />
          ) : !list || list.items.length === 0 ? (
            <div className="mt-6 bg-cream rounded-3xl border border-gold/30 p-10 text-center" data-testid="no-list">
              <p className="font-display font-bold text-navy">No prescribed list published for this grade yet</p>
              <p className="text-sm text-slate-500 mt-2">Check back soon, or request a quotation and we'll prepare it for you.</p>
            </div>
          ) : (
            <div className="mt-6 grid lg:grid-cols-[1fr_320px] gap-6 items-start">
              <div className="bg-white rounded-3xl border border-slate-200/80 overflow-hidden">
                {sections.map(([sec, items]) => (
                  <div key={sec}>
                    <p className="px-6 py-3 bg-cream text-xs font-bold uppercase tracking-wider text-navy" data-testid={`section-${sec}`}>
                      {sec === "textbooks" ? "Prescribed textbooks" : "Stationery"}
                    </p>
                    {items.map((it) => (
                      <div
                        key={it.idx}
                        className={`px-6 py-4 flex items-center gap-4 border-t border-slate-100 ${it.unavailable ? "opacity-60" : ""}`}
                        data-testid={`list-item-${it.idx}`}
                      >
                        <button
                          onClick={() => !it.unavailable && setSelected({ ...selected, [it.idx]: !selected[it.idx] })}
                          className="text-navy"
                          data-testid={`list-check-${it.idx}`}
                          aria-label={selected[it.idx] ? "Deselect" : "Select"}
                        >
                          {selected[it.idx] ? <CheckSquare size={20} className="text-gold" /> : <Square size={20} className="text-slate-300" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-navy flex items-center gap-2 flex-wrap">
                            {it.name}
                            {it.required ? (
                              <span className="text-[10px] font-bold uppercase tracking-wide bg-navy/5 text-navy px-2 py-0.5 rounded-full">Required</span>
                            ) : (
                              <span className="text-[10px] font-bold uppercase tracking-wide bg-brand/10 text-brand px-2 py-0.5 rounded-full">Optional</span>
                            )}
                            {it.unavailable && (
                              <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${it.note === "Out of stock" ? "bg-amber-50 text-amber-600" : it.note === "Price coming soon" ? "bg-sky-50 text-sky-600" : "bg-red-50 text-red-500"}`}>
                                {it.note || "Unavailable"}
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-slate-500 font-mono mt-0.5">
                            {it.unavailable ? (it.note || "Unavailable") : <>{formatR(it.effective_price)} each</>}
                            {!it.unavailable && it.on_special && <span className="text-brand ml-2">on special</span>}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-slate-400">Qty</span>
                          <input
                            type="number"
                            min="1"
                            max="99"
                            value={qtys[it.idx] || 1}
                            disabled={it.unavailable}
                            onChange={(e) => setQtys({ ...qtys, [it.idx]: Math.max(1, Math.min(99, Number(e.target.value) || 1)) })}
                            className="w-16 h-9 rounded-lg border border-slate-200 text-center text-sm font-semibold outline-none focus:ring-2 focus:ring-gold"
                            data-testid={`list-qty-${it.idx}`}
                          />
                        </div>
                        <span className="font-mono text-sm font-bold text-navy w-20 text-right">
                          {formatR((it.effective_price || 0) * (qtys[it.idx] || 1))}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <div className="bg-navy text-white rounded-3xl p-6 sticky top-36" data-testid="list-summary">
                <h3 className="font-display font-bold">List total</h3>
                <p className="text-sm text-white/60 mt-1">{totals.count} item{totals.count !== 1 ? "s" : ""} selected</p>
                <p className="font-mono font-extrabold text-2xl text-gold mt-3" data-testid="list-total">{formatR(totals.total)}</p>
                <button
                  onClick={() => addSelected(false)}
                  className="mt-5 w-full h-12 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors gold-glow"
                  data-testid="add-selected-to-cart"
                >
                  <ShoppingCart size={15} /> Add selected to cart
                </button>
                <button
                  onClick={() => addSelected(true)}
                  className="mt-3 w-full h-11 rounded-full border border-white/25 hover:bg-white/10 text-white font-bold text-sm transition-colors"
                  data-testid="add-full-pack"
                >
                  Add full grade pack
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
